import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { UserCog, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '~/client/common/stores/auth'
import { startImpersonationHelper } from '~/client/admin/impersonate.helper'

const ERROR_MESSAGES: Record<string, string> = {
  TARGET_IS_SUPERUSER: "Admins can't be impersonated.",
  USER_NOT_FOUND: 'No user found with that email or ID.',
  SELF_IMPERSONATION_FORBIDDEN: "Can't impersonate yourself.",
  FORBIDDEN: "You don't have permission to do this.",
  VALIDATION_ERROR: 'Enter a valid email or user ID.',
}

function humanizeError(message: string): string {
  return ERROR_MESSAGES[message] ?? 'Something went wrong. Try again.'
}

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/admin/impersonate')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    const { userDetail } = useAuthStore.getState()
    if (userDetail && !userDetail.is_superuser) {
      throw redirect({
        to: '/admin/recs',
        search: { eventId: undefined, tab: undefined, status: undefined, city: undefined, sort: undefined },
      })
    }
  },
  component: AdminImpersonatePage,
})

// ─── Component ────────────────────────────────────────────────────────────────

function AdminImpersonatePage() {
  const [emailOrId, setEmailOrId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleImpersonate = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = emailOrId.trim()
    if (!value) return

    setIsLoading(true)
    setError(null)

    try {
      await startImpersonationHelper(value)
      // startImpersonationHelper performs the redirect — code below is unreachable on success.
    } catch (err) {
      const raw = err instanceof Error ? err.message : ''
      setError(humanizeError(raw))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Impersonate User
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Debug a user&apos;s experience by viewing the app as them.
        </p>
      </div>

      {/* Warning Banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="size-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-amber-800">
            How impersonation works
          </p>
          <ul className="text-xs text-amber-700 leading-relaxed list-disc pl-4 space-y-1">
            <li>Anything you do affects the user&apos;s real account.</li>
            <li>Every action is logged with your admin ID.</li>
            <li>You see the app exactly as they do.</li>
            <li>Your admin session stays active.</li>
            <li>
              Click <strong>Stop</strong> in the top banner to exit.
            </li>
          </ul>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-xl border bg-card p-6">
        <form onSubmit={handleImpersonate} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="emailOrId"
              className="text-sm font-medium text-foreground"
            >
              User Email or ID
            </label>
            <Input
              id="emailOrId"
              type="text"
              placeholder="user@example.com or user ID"
              value={emailOrId}
              onChange={(e) => {
                setEmailOrId(e.target.value)
                if (error) setError(null)
              }}
              disabled={isLoading}
              error={error ?? undefined}
              className="max-w-md"
            />
            {!error && (
              <p className="text-xs text-muted-foreground max-w-md">
                Enter the user&apos;s email address or ID.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading || !emailOrId.trim()}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <span className="inline-block size-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                Starting impersonation...
              </>
            ) : (
              <>
                <UserCog className="size-4" />
                Impersonate User
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}
